import { useCallback } from "react";

import { useDispatch } from "react-redux";

import { useRequestWithFeedback, combineRunProps } from "../../../composites";

import API from "./api";
import { actions } from "./slice";
import { transformApiOfferToRewardOffer } from "./utils";

import type {
  EnrollmentStatusResponse,
  GetOffersResponse,
  RewardOffer,
} from "./types";
import type { ApiError } from "../../../../types/models/v1/Error.types";
import type { OverrideRunProps } from "../../../composites";

const { setEnrolled, setOffers, setRecommendedOffers, setCategories } = actions;

export const useFetchCardLinkedOffersEnrollment = () => {
  const dispatch = useDispatch();
  const { send, loading } = useRequestWithFeedback<
    EnrollmentStatusResponse,
    ApiError
  >();

  const fetchEnrollmentStatus = useCallback(
    (overrideRunProps: OverrideRunProps<EnrollmentStatusResponse> = {}) => {
      send({
        action: API.getCardLinkedOffersEnrollmentStatus(),
        ...combineRunProps<EnrollmentStatusResponse>(
          {
            onData: ({ is_enrolled }) => {
              dispatch(setEnrolled(is_enrolled));
            },
          },
          overrideRunProps,
        ),
      });
    },
    [send, dispatch],
  );

  return { fetchEnrollmentStatus, loading };
};

export const useDeleteCardLinkedOffersEnrollment = () => {
  const dispatch = useDispatch();
  const { send, loading } = useRequestWithFeedback<null>();

  const deleteEnrollment = useCallback(
    (overrideRunProps: OverrideRunProps<null> = {}) => {
      send({
        action: API.deleteCardLinkedOffersEnrollment(),
        ...combineRunProps<null>(
          {
            onSuccess: () => {
              dispatch(setEnrolled(false));
            },
          },
          overrideRunProps,
        ),
      });
    },
    [send, dispatch],
  );

  return { deleteEnrollment, loading };
};

export const useCreateCardLinkedOffersEnrollment = () => {
  const dispatch = useDispatch();
  const { send, loading } = useRequestWithFeedback<null>();

  const createEnrollment = useCallback(
    (overrideRunProps: OverrideRunProps<null> = {}) => {
      send({
        action: API.createCardLinkedOffersEnrollment(),
        ...combineRunProps<null>(
          {
            onSuccess: () => {
              dispatch(setEnrolled(true));
            },
          },
          overrideRunProps,
        ),
      });
    },
    [send, dispatch],
  );

  return { createEnrollment, loading };
};

export const useFetchCardLinkedOffers = () => {
  const dispatch = useDispatch();
  const { send, loading } = useRequestWithFeedback<
    GetOffersResponse,
    ApiError
  >();

  const fetchOffers = useCallback(
    (
      isRecommended = false,
      overrideRunProps: OverrideRunProps<GetOffersResponse> = {},
    ) => {
      send({
        action: API.getCardLinkedOffers(isRecommended),
        ...combineRunProps(
          {
            onData: (offers = []) => {
              const categories = new Set<string>();
              const transformedOffers: RewardOffer[] = [];

              offers.forEach((offer) => {
                transformedOffers.push(
                  transformApiOfferToRewardOffer(offer, isRecommended),
                );
                if (!isRecommended) {
                  offer.categories.forEach((category) =>
                    categories.add(category),
                  );
                }
              });

              const action = isRecommended ? setRecommendedOffers : setOffers;
              dispatch(action({ offers: transformedOffers }));
              if (!isRecommended) {
                dispatch(setCategories({ categories: Array.from(categories) }));
              }
            },
          },
          overrideRunProps,
        ),
      });
    },
    [send, dispatch],
  );

  return { fetchOffers, loading };
};
